import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, useTheme, Typography } from '@mui/material';
import personIcon from './images/person.svg';
import assignmentIcon from './images/assignment.svg';
import commentIcon from './images/comment.svg';
import checkIcon from './images/check.svg';
import accessTimeIcon from './images/access-time.svg';
import { ReportHeaderComponent } from '../../../components/reports/ReportHeaderComponent/ReportHeaderComponent';
import { ReportTableComponent, ReportTableComponentRef } from '../../../components/reports/ReportTableComponent/ReportTableComponent';
import { SelectComponent, SelectOption } from '../../../components/core/SelectComponent/SelectComponent';
import { ExportMenuComponent } from '../../../components/reports/ExportMenuComponent/ExportMenuComponent';
import {
  getReportingSchoolYears,
  ReportingSchoolYearEntry,
} from '../../../components/reports/services/reportingSchoolYearsService';
import { getReportData, ExperienceSummaryRecord, ExperienceSummaryResponse } from './ExperienceSummaryService';
import { experienceSummaryStyles } from './ExperienceSummary.styles';
import type { GridColDef } from '@mui/x-data-grid';
import { toOptionalPositiveInteger } from '../../../services/Utilities';

const WILDCARD_COLLECTION = '616C6C';

interface FormattedDataRow {
  id: number;
  archivedInfo: boolean;
  prettyName: string;
  teacherEmail: string;
  date: string;
  filterDate: Date;
  schoolName: string;
  className: string;
  experienceName: string;
  experienceId: number;
  numberOfStudents: number;
  percentComplete: number;
  averageResponses: number;
  timeOnTask: string;
  timeOnTaskSort: number;
  quizScore: number | null;
  quizSort: number;
}

function toUSDateFormat(d: Date): string {
  const month = ('0' + (d.getMonth() + 1)).slice(-2);
  const day = ('0' + d.getDate()).slice(-2);
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

function abbreviateNumber(num: number | null | undefined): string {
  if (!num) {
    return String(num ?? '');
  }
  num = Number(num) || 0;
  if (num < 1000) {
    return '' + num;
  }
  const power = Number((num.toPrecision(2).split('e')[1] || [0, 0]).slice(1));
  const triplets = Math.floor(power / 3);
  let numericResult = (num / Math.pow(10, triplets * 3)).toFixed(1);
  if (numericResult.length > 4) {
    numericResult = (num / Math.pow(10, triplets * 3)).toFixed();
  }
  return numericResult + ['', 'K', 'M', 'B', 'T'][triplets];
}

function formatExperienceSummaryDataRow(row: ExperienceSummaryRecord, index: number): FormattedDataRow {
  const date = new Date(row.ends_at);
  let quizSort = row.avg_quiz_score_of_ended;
  if (quizSort === null) {
    quizSort = -1;
  }
  let quiz = row.avg_quiz_score_of_ended;
  if (quiz !== null) {
    quiz = Math.round(quiz);
  }
  let avgResp = row.avg_responses_of_started || 0;
  if (avgResp % 1 !== 0) {
    avgResp = Math.round(avgResp * 1e1) / 1e1;
  }
  const percCompleted = Math.round(row.avg_progress || 0);
  const timeOnTask = Math.round((row.time_on_task || 0) / 60);
  return {
    id: index,
    archivedInfo: row.archived_info || false,
    prettyName: row.first_name + ' ' + row.last_name,
    teacherEmail: row.email,
    date: toUSDateFormat(date),
    filterDate: date,
    schoolName: row.school_name,
    className: row.class_name,
    experienceName: row.experience_name,
    experienceId: row.experience_id,
    numberOfStudents: row.students_started,
    percentComplete: percCompleted,
    averageResponses: avgResp,
    timeOnTask: abbreviateNumber(timeOnTask) || '-',
    timeOnTaskSort: timeOnTask || 0,
    quizScore: quiz,
    quizSort: quizSort,
  };
}

function isSPIREReport(reportKey: string): boolean {
  return [
    'subscription-school-spire-hybrid-experience-summary',
    'subscription-district-spire-hybrid-experience-summary',
    'subscription-district-spire-experience-summary',
    'subscription-school-spire-experience-summary',
  ].includes(reportKey);
}

function isSpireOrWW(collectionIds: string[]): boolean {
  return collectionIds.length > 0 && collectionIds[0] !== WILDCARD_COLLECTION;
}

interface CourseOption {
  id: string;
  name: string;
  set_order: number;
}

function getUniqueCourseList(data: ExperienceSummaryRecord[]): CourseOption[] {
  const idToName: Record<string, string> = {};
  data.forEach((rec) => {
    if (rec.collection_uuid) {
      idToName[rec.collection_uuid] = rec.collection;
    }
  });
  const uniqueCourses = Object.keys(idToName)
    .map((id) => {
      const courseRec = data.find((rec) => rec.collection_uuid === id);
      return {
        id: courseRec!.collection_uuid,
        name: courseRec!.collection,
        set_order: courseRec!.set_order,
      };
    })
    .sort((a, b) => a.set_order - b.set_order);
  return uniqueCourses;
}

interface ExperienceSummaryProps {
  reportKey?: string;
  onInited?: (value?: boolean) => void;
  onSchoolYearChange?: (selection: ReportingSchoolYearEntry | undefined) => void;
  onCourseChange?: (value: string | undefined) => void;
  isSPIRE?: boolean;
  lastUpdated?: string;
}

export const ExperienceSummary = ({
  reportKey,
  onInited,
  onSchoolYearChange,
  onCourseChange,
  isSPIRE: isSPIREProp = false,
  lastUpdated = '',
}: ExperienceSummaryProps = {}) => {
  const theme = useTheme();
  const [selectValue, setSelectValue] = useState<string | number | undefined>(undefined);
  const [schoolYearEntries, setSchoolYearEntries] = useState<ReportingSchoolYearEntry[]>([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState<ReportingSchoolYearEntry>();
  const [schoolYearsLoaded, setSchoolYearsLoaded] = useState(false);
  const [courseValue, setCourseValue] = useState<string | number | undefined>(undefined);
  // Keep the latest course available to async responses without making course selection refetch the report.
  const courseValueRef = useRef<string | number | undefined>(undefined);
  const [courseOptions, setCourseOptions] = useState<SelectOption[]>([]);
  const [tableData, setTableData] = useState<FormattedDataRow[]>([]);
  const tableRef = useRef<ReportTableComponentRef>(null);
  const [isSPIRE, setIsSPIRE] = useState<boolean>(isSPIREProp);

  useEffect(() => {
    getReportingSchoolYears()
      .then((entries) => {
        setSchoolYearEntries(entries);
        setSelectedSchoolYear(entries[0]);
        setSelectValue(entries[0]?.id);
      })
      .catch(() => {
        setSchoolYearEntries([]);
        setSelectedSchoolYear(undefined);
        setSelectValue(undefined);
      })
      .finally(() => setSchoolYearsLoaded(true));
  }, []);

  const schoolYearOptions = useMemo<SelectOption[]>(
    () => schoolYearEntries.map((entry) => ({ id: entry.id, name: entry.name })),
    [schoolYearEntries]
  );

  const handleExportClick = useCallback(() => {
    // Callback when export button is clicked - can be wired to CSV/PDF later
  }, []);

  const handleSelectChange = useCallback(
    (selectedId: string | number) => {
      const selection = schoolYearEntries.find((entry) => entry.id === selectedId);
      setSelectValue(selectedId);
      setSelectedSchoolYear(selection);
      onSchoolYearChange?.(selection);
    },
    [onSchoolYearChange, schoolYearEntries]
  );

  const handleCourseChange = useCallback(
    (selectedId: string | number | (string | number)[]) => {
      const value = Array.isArray(selectedId) ? selectedId[0] : selectedId;
      setCourseValue(value);
      courseValueRef.current = value;
      const strValue = value === undefined || value === '' ? undefined : String(value);
      onCourseChange?.(strValue);
    },
    [onCourseChange]
  );

  useEffect(() => {
    if (!schoolYearsLoaded) {
      return;
    }

    const filter = selectedSchoolYear
      ? { year: selectedSchoolYear.value, include_archived: selectedSchoolYear.archived }
      : undefined;
    let isCurrentRequest = true;

    getReportData(reportKey || 'curriculum-experience-summary', filter)
      .then((response) => {
        if (!isCurrentRequest) {
          return;
        }
        if (Array.isArray(response)) {
          onInited?.(true);
          return;
        }
        const report = response as ExperienceSummaryResponse;
        console.log('Experience summary data loaded:', report);

        setIsSPIRE(isSPIREReport(report.report_key));

        const classId = toOptionalPositiveInteger(new URLSearchParams(window.location.search).get('class_id'));
        const reportData = classId ? report.data.filter((record) => record.class_id === classId) : report.data;
        if (classId && reportData.length) {
          tableRef.current?.filterData({ items: [{ field: 'className', operator: 'equals', value: reportData[0].class_name }] });
        }
        const formattedData = reportData.map(formatExperienceSummaryDataRow);
        setTableData(formattedData);

        const nextCourseOptions = isSpireOrWW(report.collection_uuids)
          ? []
          : getUniqueCourseList(reportData).map((course) => ({
              id: course.id,
              name: course.name,
            }));
        setCourseOptions(nextCourseOptions);

        const selectedCourse = courseValueRef.current;
        if (
          selectedCourse !== undefined &&
          !nextCourseOptions.some((course) => course.id === selectedCourse)
        ) {
          courseValueRef.current = undefined;
          setCourseValue(undefined);
          onCourseChange?.(undefined);
        }

        onInited?.(true);
      })
      .catch((error) => {
        if (!isCurrentRequest) {
          return;
        }
        console.error('Failed to load experience summary data:', error);
        onInited?.(true);
      });

    // A year change reruns this effect; invalidate the older request before its handlers can update the report.
    return () => {
      isCurrentRequest = false;
    };
  }, [onCourseChange, onInited, reportKey, schoolYearsLoaded, selectedSchoolYear]);

  const tableColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'date',
        headerName: 'Date',
        width: 150,
        filterable: true,
        sortable: true,
      },
      {
        field: 'className',
        headerName: 'Class',
        flex: 0.3,
        minWidth: 120,
        filterable: true,
        sortable: true,
      },
      {
        field: 'experienceName',
        headerName: 'Experience',
        flex: 0.7,
        minWidth: 150,
        filterable: true,
        sortable: true,
      },
      {
        field: 'numberOfStudents',
        headerName: 'Students',
        width: 100,
        filterable: false,
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        renderHeader: () => (
          <Box component="img" src={personIcon} alt="Number of participating students" title="Number of participating students" sx={{ width: 24, height: 24 }} />
        ),
      },
      {
        field: 'percentComplete',
        headerName: '% Complete',
        width: 100,
        filterable: false,
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        renderHeader: () => (
          <Box component="img" src={assignmentIcon} alt="% complete" title="% complete" sx={{ width: 24, height: 24 }} />
        ),
      },
      {
        field: 'averageResponses',
        headerName: 'Avg Responses',
        width: 100,
        filterable: false,
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        renderHeader: () => (
          <Box component="img" src={commentIcon} alt="Average responses per student" title="Average responses per student" sx={{ width: 24, height: 24 }} />
        ),
      },
      {
        field: 'quizScore',
        headerName: 'Quiz Score',
        width: 100,
        filterable: false,
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        renderHeader: () => (
          <Box component="img" src={checkIcon} alt="Average quiz score" title="Average quiz score" sx={{ width: 24, height: 24 }} />
        ),
      },
      {
        field: 'timeOnTask',
        headerName: 'Time on Task',
        width: 100,
        filterable: false,
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        renderHeader: () => (
          <Box component="img" src={accessTimeIcon} alt="Time on task (min.)" title="Time on task (min.)" sx={{ width: 24, height: 24 }} />
        ),
      },
    ],
    []
  );

  const headerComponents = useMemo(
    () => [
      [
        null,
        null,
        null,
        null,
        null,
        <ExportMenuComponent
          key="export-menu"
          showPrint={false}
          showExport={true}
          onPrint={() => {}}
          onExport={handleExportClick}
        />,
      ],
      [
        null,
        null,
        null,
        null,
        null,
        <SelectComponent
          key="experience-summary-select"
          options={schoolYearOptions}
          value={selectValue}
          onChange={handleSelectChange}
          label="School Year"
        />,
      ],
      [
        null,
        null,
        null,
        null,
        null,
        <SelectComponent
          key="course-select"
          options={courseOptions}
          value={courseValue}
          onChange={handleCourseChange}
          label="Course"
        />,
      ],
    ],
    [handleExportClick, handleSelectChange, selectValue, schoolYearOptions, handleCourseChange, courseValue, courseOptions]
  );

  return (
    <Box sx={experienceSummaryStyles.container(theme)}>
      <Box sx={experienceSummaryStyles.headerContainer(theme)}>
        <ReportHeaderComponent
          rows={3}
          columns={6}
          components={headerComponents}
        />
      </Box>
      <Box sx={experienceSummaryStyles.tableContainer(theme)}>
        <ReportTableComponent
          ref={tableRef}
          data={tableData}
          layout={{
            columns: tableColumns,
            pageSize: 10,
            enableFiltering: true,
          }}
        />
      </Box>
      <Box sx={experienceSummaryStyles.legendSection(theme)}>
        <Box sx={experienceSummaryStyles.legendWell(theme)}>
          <Box sx={experienceSummaryStyles.legendRow(theme)}>
            <Box sx={experienceSummaryStyles.legendItem(theme)} title="Number of participating students">
              <Box component="img" src={personIcon} alt="" aria-hidden sx={{ width: 20, height: 20, flexShrink: 0 }} />
              <Typography component="span" variant="body2">
                Number of participating students
              </Typography>
            </Box>
            <Box sx={experienceSummaryStyles.legendItem(theme)} title="% complete">
              <Box component="img" src={assignmentIcon} alt="" aria-hidden sx={{ width: 20, height: 20, flexShrink: 0 }} />
              <Typography component="span" variant="body2">
                % complete
              </Typography>
            </Box>
            {!isSPIRE && (
              <>
                <Box sx={experienceSummaryStyles.legendItem(theme)} title="Average responses per student">
                  <Box component="img" src={commentIcon} alt="" aria-hidden sx={{ width: 20, height: 20, flexShrink: 0 }} />
                  <Typography component="span" variant="body2">
                    Average responses per student
                  </Typography>
                </Box>
                <Box sx={experienceSummaryStyles.legendItem(theme)} title="Average quiz score">
                  <Box component="img" src={checkIcon} alt="" aria-hidden sx={{ width: 20, height: 20, flexShrink: 0 }} />
                  <Typography component="span" variant="body2">
                    Average quiz score
                  </Typography>
                </Box>
              </>
            )}
            <Box sx={experienceSummaryStyles.legendItem(theme)} title="Time on task (min.)">
              <Box component="img" src={accessTimeIcon} alt="" aria-hidden sx={{ width: 20, height: 20, flexShrink: 0 }} />
              <Typography component="span" variant="body2">
                Time on task (min.)
              </Typography>
            </Box>
          </Box>
        </Box>
        {lastUpdated && (
          <Typography sx={experienceSummaryStyles.lastUpdated(theme)}>
            Last updated {lastUpdated}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ExperienceSummary;
